import React from "react";
import { PRIVACY_FLAGS, CONFIRMABLE_FACTS } from "../config/privacyFacts";

// Privacy & security — calm, plain-English, UK spelling, no em dashes, no overclaims.
// Verified project facts are kept strictly separate from platform/compliance items still
// being confirmed. Reused logged-out (Auth) and logged-in (Settings).

function Section({ title, children, tone }) {
  const titleColor = tone === "muted" ? "text-[var(--ink2)]" : "text-[var(--ink)]";
  return (
    <section className="mb-8">
      <h3 className={`font-serif-display text-[17px] ${titleColor} mb-3`}>{title}</h3>
      <div className="space-y-2.5 text-[14px] leading-relaxed text-[var(--ink2)]">{children}</div>
    </section>
  );
}

function Item({ children, tone }) {
  const dot = tone === "pending" ? "bg-[var(--ink4)]" : "bg-[var(--pine)]";
  return (
    <div className="flex gap-2.5">
      <span className={`mt-[7px] h-1.5 w-1.5 rounded-full ${dot} shrink-0`} aria-hidden="true" />
      <p className="min-w-0">{children}</p>
    </div>
  );
}

export default function TrustPrivacyContent() {
  // Items move between sections purely on config flags (see config/privacyFacts.js). With all
  // flags false the visible copy is unchanged.
  const confirmedFacts = CONFIRMABLE_FACTS.filter((f) => PRIVACY_FLAGS[f.flag]);
  const pendingFacts = CONFIRMABLE_FACTS.filter((f) => !PRIVACY_FLAGS[f.flag]);
  return (
    <div data-testid="trust-privacy-content">
      <p className="text-[14.5px] leading-relaxed text-[var(--ink2)] mb-3">
        Heldall is designed to hold your thoughts safely and only show them to the right people.
      </p>
      <p className="text-[14px] leading-relaxed text-[var(--ink3)] mb-8">
        This page explains what Heldall stores, what AI may see, who can see your items, what you can
        control, and what we are still confirming.
      </p>

      <Section title="What we can confirm today">
        <Item>Private items stay private to you.</Item>
        <Item>Shared or handed-off items are only visible to the household or person they are shared with.</Item>
        <Item>Heldall stores the text you capture, the cleaned-up item, its timing, status, type, category and sharing state.</Item>
        <Item>Your password is never stored in a readable form.</Item>
        <Item>Data is encrypted in transit over HTTPS and TLS.</Item>
        <Item>AI may receive the audio or text of what you captured to help split, sort and understand your items. When you use voice, the audio leaves your device and is sent for transcription.</Item>
        <Item>Heldall does not send your password, account ID, household ID or internal user IDs to the AI.</Item>
        <Item>Where timing matters, AI output is checked by Heldall's own rules.</Item>
        <Item>If AI is unavailable, your capture is still held and sorted by Heldall's built-in rules.</Item>
        {confirmedFacts.map((f) => (
          <Item key={f.flag}>{f.confirmed}</Item>
        ))}
      </Section>

      <Section title="What we are still confirming">
        {pendingFacts.map((f) => (
          <Item key={f.flag} tone="pending">{f.pending}</Item>
        ))}
        <Item tone="pending">Whether a DPA and sub-processor list will be available.</Item>
      </Section>

      <Section title="What we do not currently claim" tone="muted">
        <p className="text-[var(--ink3)]">
          We do not currently claim SOC 2, end-to-end encryption, formal GDPR certification, fully
          verified AI-provider retention terms, or a confirmed data residency region. We will update
          this page as each point is confirmed.
        </p>
      </Section>

      <Section title="Your controls">
        <Item>You can export a copy of your data from Account.</Item>
        <Item>Your export includes your captures and your view of handoffs.</Item>
        <Item>Your export never includes another household member's private data.</Item>
        <Item>Deleting an item first moves it to Recently Deleted. Restore brings it back.</Item>
        <Item>Delete forever permanently removes the item from Heldall's app database.</Item>
        <Item>Delete all forever only applies to items that are already deleted.</Item>
        <Item>You can close your account from Account.</Item>
      </Section>

      <Section title="Questions">
        <p>
          Questions about any of this? Reach us from Settings, under Report an issue or Share feedback.
          A real person reads it.
        </p>
      </Section>

      <p className="text-[12.5px] text-[var(--ink4)] border-t border-[var(--line)] pt-5">
        Last updated: 26 June 2026
      </p>
    </div>
  );
}
